'use client';

import { FC, useRef, useState } from 'react';

import { useGetAccountPhoto, useUploadAccountPhoto } from '@/data';

import { UPLOAD_ERROR_MESSAGE } from './constants';
import { PictureUploader } from '../PictureUploader';

const AccountPhoto: FC<IAccountPhotoProps> = ({
  value,
  loading,
  onChange,
  title,
  alt,
  field,
  rounded,
}) => {
  const [isLoading, setIsLoading] = useState(loading);
  const photo = useRef<File | null>(null);

  const { data, loading: isLoadingPhoto } = useGetAccountPhoto({
    skip: !value,
    variables: {
      filename: value,
    },
  });

  const { uploadAccountPhoto } = useUploadAccountPhoto({
    onCompleted: res => {
      import('axios').then(mod => {
        const axios = mod.default;
        const { file_name, url, headers } = res.uploadAccountPhoto ?? {};

        axios
          .put(url ?? '', photo?.current, {
            headers: {
              'Content-Type': (photo?.current as any)?.type,
              ...(headers?.reduce(
                (prev, header) => ({
                  ...prev,
                  [header?.key ?? '']: header?.value,
                }),
                {},
              ) ?? {}),
            },
          })
          .then(uploadResponse => {
            if (uploadResponse.status !== 200) {
              onChange(field, null, UPLOAD_ERROR_MESSAGE);
              return;
            }

            onChange(field, file_name, null);
          })
          .catch(e => {
            // eslint-disable-next-line no-console
            console.error(e);
            onChange(field, null, UPLOAD_ERROR_MESSAGE);
          })
          .finally(() => {
            setIsLoading(false);
          });
      });
    },
    onError: e => {
      // eslint-disable-next-line no-console
      console.error(e);
      onChange(field, null, UPLOAD_ERROR_MESSAGE);
      setIsLoading(false);
    },
  });

  const handleChange = ({ file, error }: { file?: File; error?: string }) => {
    if (error) {
      onChange(field, null, error);
      return;
    }

    if (file) {
      setIsLoading(true);
      photo.current = file;

      uploadAccountPhoto({
        variables: {
          input: {
            file_name: file.name,
            file_type: file.type,
          },
        },
      });
    }
  };

  return (
    <PictureUploader
      alt={alt}
      imageSrc={data}
      isLoading={isLoading || isLoadingPhoto}
      onChange={handleChange}
      rounded={rounded}
      title={title}
    />
  );
};

interface IAccountPhotoProps {
  alt?: string;
  field: string;
  loading?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (field: any, url: any, error: string | null) => void;
  rounded?: boolean;
  title: string;
  value: any;
}

export default AccountPhoto;
